import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  GlobeIcon,
  UserIcon,
  ShieldIcon,
  PaletteIcon,
  EyeIcon,
  EyeOffIcon,
  CopyIcon,
  RefreshCwIcon,
  LockIcon,
  Settings as SettingsGearIcon,
} from 'lucide-react';

const inputClass =
  'w-full rounded-xl border-[2px] border-slate-200/90 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200/80';

const Settings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getCurrentLanguageInfo, isRTL } = useLanguage();
  const { user } = useAuth();

  const editProfilePath = user?.role === 'STUDENT' ? '/student/edit-profile' : '/dashboard/edit-profile';

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('settings.password.copied'), {
        duration: 2000,
        position: 'top-center',
      });
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error(t('settings.password.copyError'), {
        duration: 3000,
        position: 'top-center',
      });
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('settings.password.mismatch'), {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error(t('settings.password.tooShort'), {
        duration: 3000,
        position: 'top-center',
      });
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await api.put(`/users/${user.id}/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.data.success) {
        toast.success(response.data.message || t('settings.password.success'), {
          duration: 3000,
          position: 'top-center',
        });

        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setShowPasswordForm(false);
      } else {
        throw new Error(response.data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || error.message || t('settings.password.error');

      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setShowPasswordForm(false);
  };

  const rowBtn =
    'rounded-xl border-[2px] border-sky-200/80 bg-gradient-to-br from-sky-50 to-white px-4 py-2 text-xs font-bold text-sky-800 shadow-sm transition hover:border-sky-300 hover:from-sky-100 sm:text-sm';

  const ghostRowBtn =
    'rounded-xl border-[2px] border-slate-200/90 bg-white px-4 py-2 text-xs font-bold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 sm:text-sm';

  return (
    <div
      className="mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="relative mt-5 overflow-hidden rounded-[1.5rem] border-[3px] border-sky-300/70 bg-gradient-to-br from-sky-100 via-amber-50/90 to-emerald-50 px-4 py-5 shadow-[0_8px_0_0_rgba(14,165,233,0.12)] sm:mt-6 sm:rounded-[1.75rem] sm:px-7 sm:py-6">
        <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-amber-200/45 blur-2xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-8 -left-4 h-24 w-32 rounded-full bg-sky-200/35 blur-2xl" aria-hidden />
        <div className={`relative flex flex-wrap items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border-[3px] border-white/90 bg-white shadow-[0_4px_0_0_rgba(15,23,42,0.08)] sm:h-16 sm:w-16">
            <SettingsGearIcon className="h-8 w-8 text-sky-600 sm:h-9 sm:w-9" strokeWidth={2.25} aria-hidden />
          </div>
          <div className="min-w-0 flex-1 text-start">
            <h1 className="text-xl font-black tracking-tight text-slate-900 sm:text-2xl md:text-3xl">{t('navigation.settings')}</h1>
            <p className="mt-1.5 text-sm font-semibold leading-snug text-slate-600 sm:text-base">{t('settings.description')}</p>
          </div>
        </div>
      </header>

      {/* Language — overflow-visible so the language dropdown is not clipped by the card */}
      <section className="overflow-visible rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="overflow-hidden rounded-t-[1.2rem] border-b border-slate-200/90 bg-gradient-to-r from-emerald-50/90 via-white to-sky-50/50 px-4 py-3 sm:px-5">
          <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2 border-white/80 bg-white shadow-sm">
              <GlobeIcon className="h-5 w-5 text-emerald-600" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('settings.language.title')}</h2>
              <p className="mt-0.5 text-xs font-semibold text-slate-600 sm:text-sm">{t('settings.language.description')}</p>
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-5">
          <div
            className={`flex flex-col gap-4 rounded-xl border-[2px] border-slate-200/80 bg-gradient-to-br from-slate-50/90 to-white p-4 sm:flex-row sm:items-center sm:justify-between ${isRTL ? 'sm:flex-row-reverse' : ''}`}
          >
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-500">{t('settings.language.current')}</p>
              <p className="mt-1 text-sm font-bold text-slate-900">
                <span className="text-lg">{getCurrentLanguageInfo().flag}</span>{' '}
                <span>{getCurrentLanguageInfo().name}</span>
              </p>
            </div>
            <div className="relative z-20 shrink-0 [&_button]:rounded-xl [&_button]:border-2 [&_button]:border-sky-200/80 [&_button]:bg-white [&_button]:font-semibold [&_button]:text-slate-800 [&_button]:shadow-sm [&_button]:transition [&_button]:hover:border-sky-300 [&_button]:hover:bg-sky-50/80">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </section>

      {/* User */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-amber-50/90 via-white to-violet-50/50 px-4 py-3 sm:px-5">
          <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2 border-white/80 bg-white shadow-sm">
              <UserIcon className="h-5 w-5 text-violet-600" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('settings.user.title')}</h2>
              <p className="mt-0.5 text-xs font-semibold text-slate-600 sm:text-sm">{t('settings.user.description')}</p>
            </div>
          </div>
        </div>
        <div className="space-y-3 p-4 sm:p-5">
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border-[2px] border-slate-200/80 bg-white px-4 py-3 shadow-sm ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <span className="text-sm font-bold text-slate-800">{t('settings.user.profile')}</span>
            <button type="button" onClick={() => navigate(editProfilePath)} className={rowBtn}>
              {t('common.edit')}
            </button>
          </div>
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border-[2px] border-slate-200/80 bg-white px-4 py-3 shadow-sm ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <span className="text-sm font-bold text-slate-800">{t('settings.user.password')}</span>
            <button type="button" onClick={() => setShowPasswordForm(!showPasswordForm)} className={rowBtn}>
              {showPasswordForm ? t('common.cancel') : t('common.change')}
            </button>
          </div>
        </div>
      </section>

      {/* Password form */}
      {showPasswordForm && (
        <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
          <div className="border-b border-slate-200/90 bg-gradient-to-r from-sky-50/90 via-white to-violet-50/40 px-4 py-3 sm:px-5">
            <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2 border-white/80 bg-white shadow-sm">
                <LockIcon className="h-5 w-5 text-sky-600" strokeWidth={2.25} aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('settings.password.title')}</h2>
                <p className="mt-0.5 text-xs font-semibold text-slate-600 sm:text-sm">{t('settings.password.description')}</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            <form onSubmit={handlePasswordChangeSubmit} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                  {t('settings.password.current')}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    className={`${inputClass} pe-11`}
                    placeholder={t('settings.password.currentPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute inset-y-0 end-0 flex items-center pe-3 text-slate-400 hover:text-sky-600"
                  >
                    {showPasswords.current ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="newPassword" className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                  {t('settings.password.new')}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    className={`${inputClass} pe-24`}
                    placeholder={t('settings.password.newPlaceholder')}
                    required
                  />
                  <div className="absolute inset-y-0 end-0 flex items-center pe-1">
                    <button
                      type="button"
                      onClick={() => {
                        const newPass = generatePassword();
                        handlePasswordChange('newPassword', newPass);
                        handlePasswordChange('confirmPassword', newPass);
                      }}
                      className="p-2 text-slate-400 hover:text-sky-600"
                      title={t('settings.password.generate')}
                    >
                      <RefreshCwIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(passwordData.newPassword)}
                      className="p-2 text-slate-400 hover:text-sky-600"
                      title={t('settings.password.copy')}
                    >
                      <CopyIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => togglePasswordVisibility('new')} className="p-2 text-slate-400 hover:text-sky-600">
                      {showPasswords.new ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="mb-1.5 block text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                  {t('settings.password.confirm')}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className={`${inputClass} pe-11`}
                    placeholder={t('settings.password.confirmPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute inset-y-0 end-0 flex items-center pe-3 text-slate-400 hover:text-sky-600"
                  >
                    {showPasswords.confirm ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200/90 bg-slate-50/80 px-3 py-2 text-xs font-medium text-slate-600">
                {t('settings.password.requirements')}
              </div>

              <div className={`flex flex-wrap items-center justify-end gap-3 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button type="button" onClick={handleCancelPasswordChange} disabled={isChangingPassword} className={`${ghostRowBtn} disabled:opacity-50`}>
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="rounded-xl border-2 border-sky-500/30 bg-gradient-to-r from-sky-500 to-violet-600 px-5 py-2 text-sm font-bold text-white shadow-md transition hover:opacity-95 disabled:opacity-50"
                >
                  {isChangingPassword ? (
                    <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      {t('settings.password.changing')}
                    </span>
                  ) : (
                    t('settings.password.change')
                  )}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* System (placeholders) */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-slate-50/90 via-white to-emerald-50/40 px-4 py-3 sm:px-5">
          <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2 border-white/80 bg-white shadow-sm">
              <ShieldIcon className="h-5 w-5 text-emerald-700" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('settings.system.title')}</h2>
              <p className="mt-0.5 text-xs font-semibold text-slate-600 sm:text-sm">{t('settings.system.description')}</p>
            </div>
          </div>
        </div>
        <div className="space-y-3 p-4 sm:p-5">
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border-[2px] border-slate-200/80 bg-white px-4 py-3 shadow-sm ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <span className="text-sm font-bold text-slate-800">{t('settings.system.notifications')}</span>
            <button type="button" className={ghostRowBtn}>
              {t('common.configure')}
            </button>
          </div>
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border-[2px] border-slate-200/80 bg-white px-4 py-3 shadow-sm ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <span className="text-sm font-bold text-slate-800">{t('settings.system.backup')}</span>
            <button type="button" className={ghostRowBtn}>
              {t('common.manage')}
            </button>
          </div>
        </div>
      </section>

      {/* Appearance (placeholders) */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-violet-50/90 via-white to-amber-50/40 px-4 py-3 sm:px-5">
          <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2 border-white/80 bg-white shadow-sm">
              <PaletteIcon className="h-5 w-5 text-violet-600" strokeWidth={2.25} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">{t('settings.appearance.title')}</h2>
              <p className="mt-0.5 text-xs font-semibold text-slate-600 sm:text-sm">{t('settings.appearance.description')}</p>
            </div>
          </div>
        </div>
        <div className="space-y-3 p-4 sm:p-5">
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border-[2px] border-slate-200/80 bg-white px-4 py-3 shadow-sm ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <span className="text-sm font-bold text-slate-800">{t('settings.appearance.theme')}</span>
            <button type="button" className={ghostRowBtn}>
              {t('common.customize')}
            </button>
          </div>
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border-[2px] border-slate-200/80 bg-white px-4 py-3 shadow-sm ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <span className="text-sm font-bold text-slate-800">{t('settings.appearance.layout')}</span>
            <button type="button" className={ghostRowBtn}>
              {t('common.adjust')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Settings;
